import uuid from 'uuid/v4';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project';
import { useProject } from '@atlassian/jira-business-entity-project-hook';
import {
	useExperienceStart,
	useExperienceFail,
	useExperienceSuccess,
} from '@atlassian/jira-business-experience-tracking';
import {
	IN_PROGRESS,
	StatusCategoryTypes,
} from '@atlassian/jira-common-constants/src/status-categories';
import { ff } from '@atlassian/jira-feature-flagging';
import { isClientFetchError } from '@atlassian/jira-fetch';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import {
	COLUMN_ACTION_RENAME_EXPERIENCE,
	COLUMN_ACTION_ADD_EXPERIENCE,
} from '../../common/constants';
import { convertStatusNameForComparison } from '../../common/utils/validate-column-name';
import { useBoardStore } from '../../services/board-store';
import {
	useWorkflowsV2,
	mapStatusesToStatusUpdateObjects,
	mapWorkflowToWorkflowUpdateObject,
	type StatusUpdate,
	type WorkflowStatus,
	type Transition,
} from '../../services/workflow-v2';
import { useBoardData } from '../board-data';
import { useRefetchBoardData as useRefetchBoardDataOld } from '../board-data-old';
import messages from './messages';
import {
	getColumnActionFailedFlag,
	removeWorkflowAssociatedWithSubtaskOnly,
	fireColumnExperienceTracking,
	isFilteredNetworkError,
} from './utils';

export { useDetectMultipleWorkflows } from './detect-multiple-workflows';
export { useDeleteColumn } from './delete';

const TEMPORARY_STATUS_ID = 42; // Will be overridden in the backend

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useRenameColumn = () => {
	const { fetchWorkflowsV2, validateAndUpdateWorkflowsV2 } = useWorkflowsV2();
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const [{ columns }, { modifyColumn }] = useBoardStore();
	const {
		data: { issueTypes },
	} = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});

	const startExperience = useExperienceStart(COLUMN_ACTION_RENAME_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(COLUMN_ACTION_RENAME_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(COLUMN_ACTION_RENAME_EXPERIENCE);

	const projectData = useProject();
	const projectId = String(projectData.id);
	const projectKey = projectData.key;

	let refetchBoardData: () => Promise<void>;
	if (ff('jwm.board-awesome-enhancements')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ refetch: refetchBoardData } = useBoardData());
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		refetchBoardData = useRefetchBoardDataOld();
	}

	const { showFlag } = useFlagService();
	const subtaskIssueTypeId = issueTypes.find((issueType) => issueType.subtask)?.id;
	const issueTypeIdNotSubtask = issueTypes.find((issueType) => !issueType.subtask)?.id;

	const getCleanColumnName = (name: string): string => name.replace(/\s{2,}/g, ' ').trim();

	/**
	 * @returns undefined if it is valid, otherwise return string that will be used as error message
	 * this is a pattern used by atlaskit form field validation or inline edit
	 */
	const validateRename = ({ oldName, newName }: { oldName: string; newName: string }) => {
		const cleanNewName = getCleanColumnName(newName);

		if (convertStatusNameForComparison(cleanNewName) === convertStatusNameForComparison(oldName)) {
			return undefined;
		}

		if (cleanNewName === '') {
			return formatMessage(messages.emptyColumnNameError);
		}

		if (
			columns.some(
				(col) =>
					convertStatusNameForComparison(col.name) === convertStatusNameForComparison(cleanNewName),
			)
		) {
			return formatMessage(messages.statusAlreadyExists, {
				statusName: cleanNewName,
			});
		}

		return undefined;
	};

	const renameColumn = async ({
		oldName,
		statusId,
		newName,
	}: {
		oldName: string;
		statusId: number;
		newName: string;
	}) => {
		const cleanNewName: string = getCleanColumnName(newName);

		if (cleanNewName === '') {
			return;
		}

		if (convertStatusNameForComparison(cleanNewName) === convertStatusNameForComparison(oldName)) {
			return;
		}

		startExperience();

		// set the board column to be loading status
		// optimistic update the name also
		modifyColumn(statusId, {
			loading: true,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			name: cleanNewName as string,
		});

		const revertOptimisticLoadingState = () => {
			modifyColumn(statusId, {
				loading: false,
				name: oldName,
			});
		};

		// fetch workflow data from workflow API
		let workflowsData;
		try {
			workflowsData = await fetchWorkflowsV2({
				cloudId,
				projectAndIssueTypes: issueTypes.map((it) => ({
					projectId,
					issueTypeId: it.id,
				})),
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			revertOptimisticLoadingState();
			showFlag(
				getColumnActionFailedFlag({
					messageTitle: messages.errorFlagTitleRenameColumn,
					action: 'rename',
					error,
					issueTypeIdNotSubtask,
					projectKey,
				}),
			);
			if ((error && isClientFetchError(error)) || isFilteredNetworkError(error)) {
				markExperienceSuccess();
			} else {
				markExperienceFailed('failed to fetch workflow data', error);
			}
			return;
		}

		if (!workflowsData) {
			revertOptimisticLoadingState();

			markExperienceFailed('workflow data is empty', new Error('workflow data is empty'));
			return;
		}

		// remove subtask workflow
		const filteredWorkflowsData = removeWorkflowAssociatedWithSubtaskOnly(
			workflowsData,
			subtaskIssueTypeId,
		);

		// rename the column in place
		const toRenameStatus = filteredWorkflowsData.statuses.find((s) => s.id === statusId.toString());
		if (!toRenameStatus) {
			revertOptimisticLoadingState();

			markExperienceFailed(
				'cannot find status to rename in workflow data',
				new Error('cannot find status to rename in workflow data'),
			);
			return;
		}

		toRenameStatus.name = cleanNewName;
		const updatePayload = {
			statuses: mapStatusesToStatusUpdateObjects(workflowsData.statuses),
			workflows: [mapWorkflowToWorkflowUpdateObject(workflowsData.workflows[0])],
		};

		// validate the payload and update the worfklow
		try {
			await validateAndUpdateWorkflowsV2({
				cloudId,
				payload: {
					payload: updatePayload,
					validationOptions: { levels: ['ERROR'] },
				},
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			revertOptimisticLoadingState();
			showFlag(
				getColumnActionFailedFlag({
					messageTitle: messages.errorFlagTitleRenameColumn,
					action: 'rename',
					error,
					issueTypeIdNotSubtask,
					projectKey,
				}),
			);
			fireColumnExperienceTracking({
				markExperienceSuccess,
				markExperienceFailed,
				error,
				errorMessage: 'failed to validate and update workflow',
			});
			return;
		}

		markExperienceSuccess();

		// sync the board data after rename
		await refetchBoardData();

		// since fetchBoardData will update the column name, we want to set the `loading` state to be false
		modifyColumn(statusId, {
			loading: false,
		});
	};

	return {
		renameColumn,
		validateRename,
	};
};

/** @deprecated Remove when jwm.board-awesome-enhancements is cleaned up */
export const useAddColumn = () => {
	const cloudId = useCloudId();
	const [{ columns, workflows }, { addColumn, setColumnsAndWorkflows }] = useBoardStore();
	const { fetchWorkflowsV2, validateAndUpdateWorkflowsV2 } = useWorkflowsV2();
	const {
		data: { issueTypes },
	} = useIssueTypesAndFields({
		issueOperation: 'VIEW',
	});

	const startExperience = useExperienceStart(COLUMN_ACTION_ADD_EXPERIENCE);
	const markExperienceSuccess = useExperienceSuccess(COLUMN_ACTION_ADD_EXPERIENCE);
	const markExperienceFailed = useExperienceFail(COLUMN_ACTION_ADD_EXPERIENCE);

	const projectData = useProject();
	const projectId = String(projectData.id);
	const projectKey = projectData.key;

	let refetchBoardData: () => Promise<void>;
	if (ff('jwm.board-awesome-enhancements')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		({ refetch: refetchBoardData } = useBoardData());
	} else {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		refetchBoardData = useRefetchBoardDataOld();
	}

	const { showFlag } = useFlagService();
	const subtaskIssueTypeId = issueTypes.find((issueType) => issueType.subtask)?.id;
	const revertOptimisticUpdate = () => {
		setColumnsAndWorkflows(columns, workflows);
	};
	const issueTypeIdNotSubtask = issueTypes.find((issueType) => !issueType.subtask)?.id;

	const addNewColumn = async ({ columnName }: { columnName: string }) => {
		startExperience();

		addColumn({
			name: columnName,
			untranslatedName: columnName,
			statusCategory: StatusCategoryTypes.INDETERMINATE,
			statusId: TEMPORARY_STATUS_ID,
			canCreateIssues: true,
			hasFilteredIssues: false,
			isCollapsed: false,
			loading: true,
		});

		// Fetch workflow data from workflow API
		let workflowsData;
		try {
			workflowsData = await fetchWorkflowsV2({
				cloudId,
				projectAndIssueTypes: issueTypes.map((it) => ({
					projectId,
					issueTypeId: it.id,
				})),
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			revertOptimisticUpdate();
			showFlag(
				getColumnActionFailedFlag({
					messageTitle: messages.errorFlagTitleAddColumn,
					action: 'add',
					error,
					issueTypeIdNotSubtask,
					projectKey,
				}),
			);
			if ((error && isClientFetchError(error)) || isFilteredNetworkError(error)) {
				markExperienceSuccess();
			} else {
				markExperienceFailed('failed to fetch workflow data', error);
			}
			return;
		}

		if (!workflowsData) {
			revertOptimisticUpdate();
			markExperienceFailed('workflow data is empty', new Error('workflow data is empty'));
			return;
		}

		// remove subtask workflow
		const filteredWorkflowsData = removeWorkflowAssociatedWithSubtaskOnly(
			workflowsData,
			subtaskIssueTypeId,
		);

		const { statuses, workflows: boardWorkflows } = filteredWorkflowsData;
		const workflow = boardWorkflows[0];

		// Create new status and transition
		const getUniqueTransitionId = (transitions: Array<Transition>) => {
			const largestId = Math.max(...transitions.map((t) => parseInt(t.id, 10)));
			return String(largestId + 1);
		};

		const transitionId = getUniqueTransitionId(workflow.transitions);

		// check if existing transition has same name as the new status
		const newTransitionName = workflow.transitions.some(
			(t) => convertStatusNameForComparison(t.name) === convertStatusNameForComparison(columnName),
		)
			? `${columnName} ${transitionId}`
			: columnName;

		const statusReference = uuid();

		const newStatus: StatusUpdate = {
			statusReference,
			name: columnName,
			statusCategory: IN_PROGRESS,
			description: '',
		};
		const newStatusInWorkflow: WorkflowStatus = {
			statusReference,
			properties: {},
			deprecated: false,
		};
		const newTransitionInWorkflow: Transition = {
			id: transitionId,
			type: 'GLOBAL',
			from: [],
			to: {
				statusReference,
			},
			name: newTransitionName,
			description: '',
			actions: [],
			validators: [],
			triggers: [],
			properties: {},
		};

		// Append newly created status and transition into workflow data
		workflow.statuses = [...workflow.statuses, newStatusInWorkflow];
		workflow.transitions = [...workflow.transitions, newTransitionInWorkflow];

		const updatePayload = {
			statuses: [...mapStatusesToStatusUpdateObjects(statuses), newStatus],
			workflows: [mapWorkflowToWorkflowUpdateObject(workflow)],
		};

		// validate the payload and update the worfklow
		try {
			await validateAndUpdateWorkflowsV2({
				cloudId,
				payload: {
					payload: updatePayload,
					validationOptions: { levels: ['ERROR'] },
				},
			});
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			revertOptimisticUpdate();
			showFlag(
				getColumnActionFailedFlag({
					messageTitle: messages.errorFlagTitleAddColumn,
					action: 'add',
					error,
					issueTypeIdNotSubtask,
					projectKey,
				}),
			);
			fireColumnExperienceTracking({
				markExperienceSuccess,
				markExperienceFailed,
				error,
				errorMessage: 'failed to validate and update workflow',
			});
			return;
		}

		markExperienceSuccess();

		// sync the board data after adding column
		await refetchBoardData();
	};

	return {
		addNewColumn,
	};
};
